import type { FormConfig, PixelConfig } from '../types';
import { allForms, identifyFromUrl } from './registry';
import { log } from '../utils';
import { pushFormSubmission } from '../analytics';
import { loadJourney } from '../journey';

/**
 * Form submissions arrive via postMessage from the form provider's iframe.
 * Different vendors use different message shapes, so we accept a few patterns
 * and only trust messages from registered allowedOrigins.
 */

const SUBMISSION_COOLDOWN_MS = 10_000;
const lastSubmitAt = new Map<string, number>();

function allowedOrigins(): string[] {
  return Array.from(new Set(allForms().map((f) => f.allowedOrigin)));
}

function findFormFromEvent(event: MessageEvent): FormConfig | null {
  // Try to match by iframe contentWindow first (most reliable)
  if (!event.source) return null;
  const iframes = document.querySelectorAll<HTMLIFrameElement>('iframe');
  for (const iframe of iframes) {
    if (iframe.contentWindow === event.source) {
      return identifyFromUrl(iframe.src);
    }
  }
  return null;
}

function isSubmissionMessage(data: unknown): boolean {
  if (!data || typeof data !== 'object') return false;
  const d = data as Record<string, unknown>;
  return (
    d.type === 'form_submitted' ||
    d.event === 'submit' ||
    d.action === 'submit' ||
    d.type === 'fs_form_submission'
  );
}

export function installSubmissionListener(config: PixelConfig): void {
  if (typeof window === 'undefined') return;
  const origins = allowedOrigins();

  window.addEventListener('message', (event) => {
    if (!origins.includes(event.origin)) return;
    if (!isSubmissionMessage(event.data)) return;

    const form = findFormFromEvent(event);
    if (!form) {
      log('submission from unknown iframe', event.origin);
      return;
    }

    // Cooldown to dedupe noisy providers
    const now = Date.now();
    const last = lastSubmitAt.get(form.id) ?? 0;
    if (now - last < SUBMISSION_COOLDOWN_MS) {
      log('submission deduped for', form.id);
      return;
    }
    lastSubmitAt.set(form.id, now);

    const journey = loadJourney();
    pushFormSubmission(form, journey, config);
    log('submission tracked for', form.id);
  });
}
